import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Star, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export function StarboardPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const starboardPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'starboard');
  const isEnabled = starboardPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'starboard') : pluginApi.disablePlugin(serverId, 'starboard'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'starboard', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    starboardChannelId: '',
    starEmoji: '⭐',
    threshold: 3,
    selfStar: false,
    allowNSFW: false,
    maxAge: 7,
    excludedChannels: []
  });

  useEffect(() => {
    if (starboardPlugin?.config) {
      setConfig({ ...config, ...starboardPlugin.config });
    }
  }, [starboardPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  if (!starboardPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-500/20 rounded-xl">
            <Star className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Starboard</h3>
            <p className="text-sm text-muted-foreground">
              Highlight popular messages with stars.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <>
          {/* Basic Settings */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-amber-400" />
              <h4 className="font-semibold">Starboard Settings</h4>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Starboard Channel</Label>
                <Select
                  value={config.starboardChannelId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, starboardChannelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona canale starboard" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun canale</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Where starred messages will be posted.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Star Emoji</Label>
                  <Input
                    placeholder="⭐"
                    value={config.starEmoji}
                    onChange={(e) => setConfig({ ...config, starEmoji: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Star Threshold</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={config.threshold}
                    onChange={(e) => setConfig({ ...config, threshold: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Stars needed to appear on board
                  </p>
                </div>
              </div>

              <div>
                <Label>Max Message Age (days)</Label>
                <Input
                  type="number"
                  min="0"
                  max="30"
                  value={config.maxAge}
                  onChange={(e) => setConfig({ ...config, maxAge: parseInt(e.target.value) })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Messages older than this won't be starred (0 = no limit)
                </p>
              </div>
            </div>
          </div>

          {/* Behavior */}
          <div className="glass-card p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              <h4 className="font-semibold">Behavior</h4>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Allow Self-Stars</Label>
                  <p className="text-xs text-muted-foreground">Let users star their own messages</p>
                </div>
                <Switch
                  checked={config.selfStar}
                  onCheckedChange={(checked) => setConfig({ ...config, selfStar: checked })}
                />
              </div>

              <div className="flex items-center justify-between py-2 px-4 bg-white/5 rounded-lg">
                <div>
                  <Label className="text-sm">Allow NSFW Channels</Label>
                  <p className="text-xs text-muted-foreground">Star messages from NSFW channels</p>
                </div>
                <Switch
                  checked={config.allowNSFW}
                  onCheckedChange={(checked) => setConfig({ ...config, allowNSFW: checked })}
                />
              </div>
            </div>
          </div>

          {/* Commands Info */}
          <div className="glass-card p-6 space-y-4">
            <h4 className="font-semibold mb-2">Available Commands</h4>
            <div className="p-3 bg-white/5 rounded-lg">
              <code className="text-amber-400">/starboard [top|recent]</code>
              <p className="text-muted-foreground mt-1">View top starred or recent starred messages</p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
